import { useState } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { useSwitchOrg } from '@/lib/useSwitchOrg'

export function DemoJoinSuccessModal({ onClose, onSwitch }: { onClose: () => void; onSwitch: () => void }) {
  return (
    <Modal track="demo_join_success" title="Welcome to the Acme Demo Org" onClose={onClose}>
      <p className="guide-para">
        You've been added as a <strong>reader</strong> to the{' '}
        <code style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--accent)' }}>acme-demo</code> organisation.
        As a reader you have read-only access to:
      </p>
      <ul className="guide-list">
        <li>Modules - browse published Terraform modules</li>
        <li>State Files - view workspace state entries</li>
        <li>Providers - serve upstream and terramantle-built provider versions, with scan results</li>
        <li>Dependency Graph - explore module relationships</li>
      </ul>
      <div style={{ background: 'var(--accent-d)', border: '1px solid color-mix(in srgb, var(--accent-fill) 20%, transparent)', borderRadius: 6, padding: '10px 14px', fontFamily: 'var(--body)', fontSize: '0.8rem', color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
        <strong>For illustrative purposes only.</strong> This is shared demo data and is not a real organisation.
        Data may be reset periodically.
      </div>
      <p style={{ fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text2)', margin: 0, lineHeight: 1.6 }}>
        To leave: <strong>Organisations → acme-demo → Leave organisation</strong>.
      </p>
      <div className="modal-actions">
        <button className="btn btn-sm" onClick={onClose}>Dismiss</button>
        <button className="btn btn-primary btn-sm" onClick={onSwitch}>Switch now →</button>
      </div>
    </Modal>
  )
}

export function DemoDataButton() {
  const { orgs, refetchOrgs } = useSelectedOrg()
  const switchOrg = useSwitchOrg()
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [demoSlug, setDemoSlug] = useState('')

  const alreadyInDemo = orgs.some((o) => o.is_demo === 1)
  if (alreadyInDemo && !showSuccess) return null

  const join = async () => {
    setLoading(true)
    try {
      const res = await api.joinDemoOrg()
      setDemoSlug(res.org.slug)
      refetchOrgs()
      setShowSuccess(true)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load demo data', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {showSuccess && demoSlug && (
        <DemoJoinSuccessModal
          onClose={() => setShowSuccess(false)}
          onSwitch={() => { setShowSuccess(false); switchOrg(demoSlug) }}
        />
      )}
      <Tooltip text="Add yourself to a dedicated demo org with sample modules, state and providers to explore the Terramantle features easily" placement="bottom">
        <button
          className="btn btn-blue btn-sm"
          onClick={join}
          disabled={loading}
          style={{ opacity: loading ? 0.6 : 1 }}
        >
          {loading ? '…' : 'Demo Org invite'}
        </button>
      </Tooltip>
    </>
  )
}

